import React, { FC, useCallback, useRef } from 'react';
import { getBiggestAndSmallestDates, getBookingDatesInBetween } from '../../helpers';
import { FORM_RADIO_COMPONENT_TYPES, FORM_RADIO_TYPES, FormDateAndTime } from '../../types';
interface FormRadioDateUntilInputProps {
  type: keyof typeof FORM_RADIO_COMPONENT_TYPES;
  formOptions: FormDateAndTime;
  bookings: Record<string, any>;
  checkIfAvailable: (fullDate: Date) => boolean;
  booking: Record<string, any>;
  loading: boolean;
  watchedDates: (Date | string)[][];
  session: number;
}

const FormRadioDateUntilInput: FC<FormRadioDateUntilInputProps> = ({ type, formOptions, checkIfAvailable, loading, booking, watchedDates, session, bookings }) => {
  const { registerAs, requiredMessage, errorMessage } = FORM_RADIO_COMPONENT_TYPES[type];
  const { register, setValue } = formOptions;
  const watchedDatesSession = watchedDates[session] || [];
  const newDateRef = useRef<string | null>(null);
  const setNewDates = useCallback(
    (dates: (Date | string)[]) => {
      const { biggestDate, smallestDate } = getBiggestAndSmallestDates(dates);
      const newDates = [smallestDate, biggestDate];
      const bookingDatesInBetween = getBookingDatesInBetween(bookings, newDates);

      const shouldReset = bookingDatesInBetween.some(booking => !checkIfAvailable(booking.fullDate) || booking.slots?.[0]?.isCheckoutOnly || smallestDate === biggestDate);

      if (shouldReset) {
        setValue(`${registerAs}.${session}`, [newDateRef.current]);
        newDateRef.current = null;
        return;
      }

      setValue(`${registerAs}.${session}`, newDates);
    },
    [bookings, checkIfAvailable, setValue, session, registerAs]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedDate = e.target.value;
      newDateRef.current = selectedDate;

      if (watchedDatesSession.length < 2) {
        setNewDates([...watchedDatesSession, selectedDate]);
        return;
      }
      const { biggestDate, smallestDate } = getBiggestAndSmallestDates(watchedDatesSession);
      const isSelectedDateAfterLastDate = new Date(biggestDate).getTime() < new Date(selectedDate).getTime();

      const isSelectedDateBeforeFirstDate = new Date(smallestDate).getTime() > new Date(selectedDate).getTime();

      if (isSelectedDateAfterLastDate) {
        setNewDates([smallestDate, selectedDate]);
        return;
      }

      if (isSelectedDateBeforeFirstDate) {
        setNewDates([selectedDate, biggestDate]);
        return;
      }

      setNewDates([selectedDate]);
    },
    [setNewDates, watchedDatesSession, bookings]
  );

  const isDateSelected = watchedDatesSession.some(date => date === new Date(booking.fullDate).toDateString());

  const isDisabled = loading || !checkIfAvailable(booking.fullDate);

  return (
    <input
      {...register(`${registerAs}.${session}`, {
        required: requiredMessage,
        validate: (value: (typeof booking)['fullDate']) => {
          if (type === FORM_RADIO_TYPES.DATE_UNTIL) {
            if (value.some((v: (typeof booking)['fullDate']) => !checkIfAvailable(v)) || watchedDates.some(date => date.length !== 2) || watchedDates.some(date => date[0] === date[1])) {
              return errorMessage;
            }
          }
        }
      })}
      type="checkbox"
      value={new Date(booking.fullDate).toDateString()}
      onChange={handleChange}
      checked={isDateSelected}
      disabled={isDisabled}
    />
  );
};

export default FormRadioDateUntilInput;
